import {ExtractionJob, PersistedExtractionJob} from "@/types";
import {subtitlesToSrt} from "@/utils/subtitleUtils";
import axios from "axios";
import {downloadTextFile, stripFileExtension, triggerDownload} from "@/utils/fileUtils";
import moment from "moment";
import {deleteJob} from "@/services/extractionJobManager";
import {Subtitle} from "@/models/Subtitle";
import {SubtitleData, SubtitleWithBounds} from "@/models/SubtitleData";

export async function addTranslation(jobId: number, translated: Subtitle[], language: string) {
    const content = subtitlesToSrt(translated);
    const url = route('subtitle.addLanguage', jobId);
    try {
        await axios.post(url, {content, language});
    } catch (err: any) {
        const msg = err.response?.data?.message || 'Failed to add translation';
        throw new Error(msg);
    }
}

export async function getSubtitleData(
    jobId: number,
    language: string = ''
): Promise<SubtitleData> {
    return fetch(route('subtitle.content', {job: jobId, language}))
        .then(response => response.json())
        .then(data => SubtitleData.deserialize(data))
}

export async function updateSubtitleData(jobId: number, data: SubtitleData, language: string) {
    const url = route('subtitle.updateContent', jobId);
    const serializedData = data.serialize();
    await axios.put(url, { data: serializedData, language });
}

/**
 * Best-effort save used during page unload. Uses fetch with keepalive so the
 * request survives the document tearing down. Subject to a 64 KB body limit
 * and cannot surface errors to the user.
 */
export async function updateSubtitleDataOnUnload(jobId: number, data: SubtitleData, language: string) {
    const url = route('subtitle.updateContent', jobId);
    const serializedData = data.serialize();
    await fetch(url, {
        method: 'PUT',
        keepalive: true,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': window.csrfToken(),
        },
        body: JSON.stringify({ data: serializedData, language }),
    });
}

export async function downloadSubtitles(
    job: PersistedExtractionJob,
    language: string | null = null,
    format: string = 'srt',
    options: Record<string, any> = {}
) {
    const filename = `${stripFileExtension(job.file_name)}${language ? '-' + language : ''}.${format}`;
    const routeUrl = route('job.download', { job: job.id, format, language, options });
    try {
        const response = await axios.get(routeUrl);
        const data = response!.data;
        const content = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
        downloadTextFile(content, filename);
    } catch (error: any) {
        const message = error.response?.data?.message || 'Failed to download subtitles';
        throw new Error(message);
    }
}

export async function bulkDownloadSubtitles(
    jobId: number[],
    languages: string[] = [],
    format: string = 'srt',
    options: { [key: string]: any } = {}
) {
    const routeUrl = route('job.bulk-download')
    try {
        const response = await axios.post(routeUrl, {
            ids: jobId,
            languages,
            format,
            options
        }, {
            responseType: 'blob',
            timeout: 60 * 1000
        })

        const blob = new Blob([response.data], { type: 'application/zip' })
        const url = window.URL.createObjectURL(blob)
        const languageSuffix = languages.length === 1 && languages[0] !== 'original' ? languages[0] + '-' : ''
        const fileName = `subtitles-${languageSuffix}${moment().format('YYYY-MM-DD-HH-mm-ss')}.zip`
        triggerDownload(url, fileName)
        window.URL.revokeObjectURL(url)
    } catch (error: any) {
        throw await parseBlobError(error);
    }
}

export function deleteSubtitle(job: ExtractionJob, language?: string) {
    if (language) {
        return axios.delete(route('subtitle.deleteLanguage', job.id), {
            data: { language }
        }).catch(err => {
            console.error(err)
            throw err
        })
    } else {
        return deleteJob(job)
    }
}

async function parseBlobError(error: any): Promise<Error> {
    if (error.response?.data instanceof Blob) {
        try {
            const text = await error.response.data.text();
            const { message } = JSON.parse(text);
            return new Error(message);
        } catch {
            return error;
        }
    }
    return error;
}

export async function getOriginalSubtitleBounds(jobId: number): Promise<SubtitleWithBounds[]> {
    return fetch(route('subtitle.getBounds', {job: jobId}))
        .then(response => response.json());
}
